import { useFeatureDiscovery } from '@confluence/feature-discovery';

import type { ListItemProps } from './ListItem';
import { CheckIconStatus } from './ListItem';
import { PremiumFeatureKey } from './useTrackPremiumFeatureUsage';

type usePremiumFeatureUsageProps = {
	skip?: boolean;
};

type usePremiumFeatureUsageIconProps = {
	shouldUpdate: boolean;
	rawSharedFeaturesMapper: Array<ListItemProps & { featureKey: PremiumFeatureKey }>;
};

export const usePremiumFeatureUsage = ({ skip = false }: usePremiumFeatureUsageProps = {}) => {
	/**
	 * if isFeatureNeverUsed is true, the feature has not been discovered
	 * else the feature has been discovered
	 */
	const [isAdvancedPermissionsNeverUsed] = useFeatureDiscovery(
		PremiumFeatureKey.ADVANCED_PERMISSIONS,
		/* skip */ skip,
	);
	const [isAnalyticsNeverUsed] = useFeatureDiscovery(PremiumFeatureKey.ANALYTICS, /* skip */ skip);
	const [isAutomationNeverUsed] = useFeatureDiscovery(
		PremiumFeatureKey.AUTOMATION,
		/* skip */ skip,
	);
	const [isTeamCalendarNeverUsed] = useFeatureDiscovery(
		PremiumFeatureKey.TEAM_CALENDAR,
		/* skip */ skip,
	);

	const featureUsageMapper = {
		[PremiumFeatureKey.ADVANCED_PERMISSIONS]: isAdvancedPermissionsNeverUsed,
		[PremiumFeatureKey.ANALYTICS]: isAnalyticsNeverUsed,
		[PremiumFeatureKey.AUTOMATION]: isAutomationNeverUsed,
		[PremiumFeatureKey.TEAM_CALENDAR]: isTeamCalendarNeverUsed,
	};

	const totalFeaturesUsed = skip
		? 0
		: Object.values(featureUsageMapper).reduce(
				(count, value) => count + (value === false ? 1 : 0),
				0,
			);

	return {
		featureUsageMapper,
		totalFeaturesUsed,
	};
};

export const usePremiumFeatureUsageIcon = ({
	shouldUpdate,
	rawSharedFeaturesMapper,
}: usePremiumFeatureUsageIconProps) => {
	const { totalFeaturesUsed, featureUsageMapper } = usePremiumFeatureUsage();

	const analyticsMapper = {
		[PremiumFeatureKey.ADVANCED_PERMISSIONS]: 'pfPagePermissionsUsed',
		[PremiumFeatureKey.ANALYTICS]: 'pfAnalyticsUsed',
		[PremiumFeatureKey.AUTOMATION]: 'pfAutomationUsed',
		[PremiumFeatureKey.TEAM_CALENDAR]: 'pfTeamCalenderUsed',
	};

	const pto2Attributes = {};
	Object.keys(featureUsageMapper).map(
		(fName) =>
			featureUsageMapper[fName] === false && (pto2Attributes[analyticsMapper[fName]] = true),
	);

	const updatedSharedFeaturesMapperWithCheckIconStatus = shouldUpdate
		? rawSharedFeaturesMapper.map((obj) => ({
				...obj,
				checkIconStatus: featureUsageMapper[obj.featureKey]
					? CheckIconStatus.UNCHECKED
					: CheckIconStatus.CHECKED,
			}))
		: rawSharedFeaturesMapper;

	return {
		updatedSharedFeaturesMapperWithCheckIconStatus,
		totalFeaturesUsed,
		pto2Attributes,
	};
};
